import {
  Dialog,
  DialogActions as MaterialDialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import {Tag} from '@wandb/weave/components/Tag';
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useHistory} from 'react-router-dom';
import styled from 'styled-components';

import {parseRefMaybe, WeaveObjectRef} from '../../../../../../../react';
import {useWeaveflowCurrentRouteContext} from '../../../context';
import {CreatePromptDrawer} from '../../../prompts/CreatePromptDrawer';
import {createNewPrompt} from '../../../prompts/promptOperations';
import {Messages} from '../../ChatView/types';
import {CopyableId} from '../../common/Id';
import {WEAVE_REF_SCHEME} from '../../wfReactInterface/constants';
import {useWFHooks} from '../../wfReactInterface/context';
import {TraceObjSchemaForBaseObjectClass} from '../../wfReactInterface/objectClassQuery';
import {useGetTraceServerClientContext} from '../../wfReactInterface/traceServerClientContext';
import {projectIdFromParts} from '../../wfReactInterface/tsDataModelHooks';
import {
  objectVersionKeyToRefUri,
  refUriToObjectVersionKey,
} from '../../wfReactInterface/utilities';
import {LLMMaxTokensKey} from '../llmMaxTokens';
import {
  OptionalTraceCallSchema,
  PlaygroundState,
  SavedPlaygroundModelState,
} from '../types';
import {
  DEFAULT_PLAYGROUND_STATE,
  DEFAULT_SAVED_MODEL,
  NEW_PROMPT_FLAG,
} from '../usePlaygroundState';
import {LLMDropdown, ProviderOption} from './LLMDropdown';
import {setLastUsedModel} from './LLMDropdown/modelCache';
import {
  PromptDropdown,
  PromptDropdownRef,
  PromptOption,
} from './PromptDropdown';
import {
  SetPlaygroundStateFieldFunctionType,
  TraceCallOutput,
} from './useChatFunctions';

type PlaygroundChatTopBarProps = {
  idx: number;
  settingsTab: number | null;
  setSettingsTab: (tab: number | null) => void;
  setPlaygroundStateField: SetPlaygroundStateFieldFunctionType;
  entity: string;
  project: string;
  playgroundStates: PlaygroundState[];
  setPlaygroundStates: (playgroundStates: PlaygroundState[]) => void;
  isTeamAdmin: boolean;
  refetchConfiguredProviders: () => void;
  refetchCustomLLMs: () => void;
  llmDropdownOptions: ProviderOption[];
  areProvidersLoading: boolean;
  customProvidersResult: TraceObjSchemaForBaseObjectClass<'Provider'>[];
  promptOptions: PromptOption[];
  promptsLoading: boolean;
  refetchPrompts: () => void;
  originalPromptMessages: MutableRefObject<Record<number, Messages>>;
};

const DialogActions = styled(MaterialDialogActions)<{$align: string}>`
  justify-content: ${({$align}) =>
    $align === 'left' ? 'flex-start' : 'flex-end'} !important;
  padding: 32px 32px 32px 32px !important;
`;
DialogActions.displayName = 'S.DialogActions';

export const PlaygroundChatTopBar: React.FC<PlaygroundChatTopBarProps> = ({
  idx,
  settingsTab,
  setSettingsTab,
  setPlaygroundStateField,
  entity,
  project,
  playgroundStates,
  setPlaygroundStates,
  isTeamAdmin,
  refetchConfiguredProviders,
  refetchCustomLLMs,
  llmDropdownOptions,
  areProvidersLoading,
  customProvidersResult,
  promptOptions,
  promptsLoading,
  refetchPrompts,
  originalPromptMessages,
}) => {
  const history = useHistory();
  const onlyOneChat = playgroundStates.length === 1;
  const [confirmClear, setConfirmClear] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmPromptChange, setConfirmPromptChange] = useState(false);
  const [pendingPromptAction, setPendingPromptAction] = useState<{
    type: 'change' | 'clear' | 'reset';
    promptName?: string;
    messages?: any[];
    promptRef?: string;
  } | null>(null);
  const promptDropdownRef = useRef<PromptDropdownRef>(null);

  // CreatePromptDrawer state
  const [isCreatePromptDrawerOpen, setIsCreatePromptDrawerOpen] =
    useState(false);
  const [createPromptMessages, setCreatePromptMessages] = useState<Messages>(
    []
  );
  const [isCreatingPrompt, setIsCreatingPrompt] = useState(false);

  // Actions menu state
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  const getTsClient = useGetTraceServerClientContext();
  const router = useWeaveflowCurrentRouteContext();
  const {useObjCreate} = useWFHooks();
  const objCreate = useObjCreate();

  const clearCall = (index: number) => {
    history.push(`/${entity}/${project}/weave/playground`);
    setPlaygroundStateField(index, 'traceCall', {
      project_id: `${entity}/${project}`,
      id: '',
      inputs: {
        messages: [],
      },
    } as OptionalTraceCallSchema);
  };

  const handleModelChange = (
    index: number,
    model: LLMMaxTokensKey,
    maxTokens: number,
    maxTokensDefault: number,
    savedModel?: SavedPlaygroundModelState
  ) => {
    // Persist the model selection to localStorage
    setLastUsedModel(entity, project, model);

    if (!savedModel) {
      setPlaygroundStates(
        playgroundStates.map((state, i) => {
          if (i === index) {
            return {
              ...state,
              model,
              maxTokensLimit: maxTokens,
              maxTokens: maxTokensDefault,
            };
          }
          return state;
        })
      );
      return;
    }

    const {messagesTemplate, ...defaultParams} =
      savedModel?.savedModelParams ?? {};

    // Filter out undefined values from defaultParams
    const filteredDefaultParams = Object.fromEntries(
      Object.entries(defaultParams).filter(([_, value]) => value !== undefined)
    );

    setPlaygroundStates(
      playgroundStates.map((state, i) => {
        if (i === index) {
          const newState = {
            ...state,
            model,
            maxTokensLimit: maxTokens,
            maxTokens: maxTokensDefault,

            // Update the state to show we are using a saved model
            savedModel: savedModel ?? DEFAULT_SAVED_MODEL,
            traceCall: {
              ...state.traceCall,
              inputs: {
                ...state.traceCall?.inputs,
                // If the saved model has messages, use them, otherwise use the current messages
                messages: messagesTemplate ?? state.traceCall?.inputs?.messages,
              },
              output: {
                ...(state.traceCall?.output as TraceCallOutput),
                // If the saved model has messages, clear the choices, otherwise use the current choices
                choices: messagesTemplate
                  ? undefined
                  : (state.traceCall?.output as TraceCallOutput)?.choices,
              },
            },
            // Update the other params
            ...filteredDefaultParams,
          };

          return newState;
        }
        return state;
      })
    );
  };

  const ConfirmClearModal: React.FC<{
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
  }> = ({open, onClose, onConfirm}) => {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Clear chat history</DialogTitle>
        <DialogContent style={{overflow: 'hidden'}}>
          <p>Are you sure you want to clear the chat history?</p>
        </DialogContent>
        <DialogActions $align="left">
          <Button variant="destructive" onClick={onConfirm}>
            Clear history
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const ConfirmPromptChangeModal: React.FC<{
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
  }> = ({open, onClose, onConfirm}) => {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Unsaved prompt changes</DialogTitle>
        <DialogContent style={{overflow: 'hidden'}}>
          <p>
            You have unsaved changes to this prompt. If you continue, your
            changes will be lost.
          </p>
        </DialogContent>
        <DialogActions $align="left">
          <Button variant="destructive" onClick={onConfirm}>
            Continue
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handlePromptChange = useCallback(
    (promptName: string, messages: any[], promptRef: string) => {
      // Clear the original messages when selecting a new prompt
      if (originalPromptMessages.current[idx]) {
        delete originalPromptMessages.current[idx];
      }

      // Update the prompt state
      setPlaygroundStateField(idx, 'prompt', {
        promptName,
        promptRef,
        messages: JSON.parse(JSON.stringify(messages)), // Deep copy for template preservation
        isModified: false, // Reset modified flag when loading a new prompt
      });
    },
    [idx, setPlaygroundStateField, originalPromptMessages]
  );

  const handlePromptChangeWithWarning = useCallback(
    (promptName: string, messages: any[], promptRef: string) => {
      const isModified = playgroundStates[idx].prompt?.isModified;

      if (isModified) {
        // Close the dropdown before showing the modal
        promptDropdownRef.current?.close();
        // Store the pending action and show the warning modal
        setPendingPromptAction({
          type: 'change',
          promptName,
          messages,
          promptRef,
        });
        setConfirmPromptChange(true);
      } else {
        // No modifications, proceed directly
        handlePromptChange(promptName, messages, promptRef);
      }
    },
    [idx, playgroundStates, handlePromptChange]
  );

  const handlePromptClearWithWarning = useCallback(() => {
    const isModified = playgroundStates[idx].prompt?.isModified;

    if (isModified) {
      // Close the dropdown before showing the modal
      promptDropdownRef.current?.close();
      // Store the pending clear action and show the warning modal
      setPendingPromptAction({
        type: 'clear',
      });
      setConfirmPromptChange(true);
    } else {
      // No modifications, proceed directly
      handlePromptChange('', [], '');
    }
  }, [idx, playgroundStates, handlePromptChange]);

  const handleReset = useCallback(() => {
    const originalMessages = originalPromptMessages.current[idx];
    if (originalMessages) {
      // Restore the original messages
      setPlaygroundStateField(idx, 'prompt', (prevPrompt: any) => ({
        ...prevPrompt,
        messages: JSON.parse(JSON.stringify(originalMessages)), // Deep copy
        isModified: false,
      }));
    }
  }, [idx, setPlaygroundStateField, originalPromptMessages]);

  const handleConfirmPromptChange = useCallback(() => {
    if (!pendingPromptAction) {
      return;
    }

    if (pendingPromptAction.type === 'change') {
      // Execute the pending prompt change
      handlePromptChange(
        pendingPromptAction.promptName!,
        pendingPromptAction.messages!,
        pendingPromptAction.promptRef!
      );
    } else if (pendingPromptAction.type === 'clear') {
      // Execute the pending clear action
      handlePromptChange('', [], '');
    } else if (pendingPromptAction.type === 'reset') {
      // Execute the pending reset action
      handleReset();
    }

    // Close modal and clear pending action
    setConfirmPromptChange(false);
    setPendingPromptAction(null);
  }, [pendingPromptAction, handlePromptChange, handleReset]);

  const handleResetWithWarning = useCallback(() => {
    const isModified = playgroundStates[idx].prompt?.isModified;

    if (isModified) {
      // Store the pending reset action and show the warning modal
      setPendingPromptAction({
        type: 'reset',
      });
      setConfirmPromptChange(true);
    }
  }, [idx, playgroundStates]);

  const handleSaveAsNew = useCallback(() => {
    const state = playgroundStates[idx];
    const messages = state.prompt?.messages || [];
    setCreatePromptMessages(messages);
    setIsCreatePromptDrawerOpen(true);
  }, [idx, playgroundStates]);

  const handleSave = useCallback(async () => {
    const state = playgroundStates[idx];
    const {promptRef, messages, promptName} = state.prompt;

    // Check if this is a "New prompt" - open CreatePromptDrawer
    if (promptName === NEW_PROMPT_FLAG || !promptRef) {
      setCreatePromptMessages(messages || []);
      setIsCreatePromptDrawerOpen(true);
      return;
    }

    if (!messages) {
      toast('Cannot save: No messages to save', {type: 'error'});
      return;
    }

    // Parse the weave URI to extract object ID using proper utility
    const objectVersionKey = refUriToObjectVersionKey(promptRef);
    if (
      !objectVersionKey ||
      objectVersionKey.scheme !== WEAVE_REF_SCHEME ||
      objectVersionKey.weaveKind !== 'object'
    ) {
      toast('Cannot save: Invalid prompt reference', {type: 'error'});
      return;
    }

    const {
      entity: entityName,
      project: projectName,
      objectId,
    } = objectVersionKey;

    setIsSaving(true);
    try {
      const tsClient = getTsClient();

      // Create a new version of the prompt (same objectId = new version)
      const createResponse = await tsClient.objCreate({
        obj: {
          project_id: projectIdFromParts({
            entity: entityName,
            project: projectName,
          }),
          object_id: objectId,
          val: {
            name: objectId,
            description: null,
            _type: 'MessagesPrompt',
            _class_name: 'MessagesPrompt',
            _bases: ['Prompt', 'Object', 'BaseModel'],
            messages,
          },
        },
      });

      // Build the new prompt ref with the returned digest
      const newPromptRef = objectVersionKeyToRefUri({
        scheme: WEAVE_REF_SCHEME,
        entity: entityName,
        project: projectName,
        weaveKind: 'object',
        objectId,
        versionHash: createResponse.digest,
        path: '',
        refExtra: '',
      });

      // Deep copy messages to prevent mutation
      const savedMessages = JSON.parse(JSON.stringify(messages));

      // Optimistic update: immediately update state with new version
      setPlaygroundStateField(idx, 'prompt', {
        promptName, // Keep the base name (version number will update on refetch)
        promptRef: newPromptRef,
        messages: savedMessages,
        isModified: false,
      });

      // Update original messages with the newly saved version
      originalPromptMessages.current[idx] = savedMessages;

      // Show success immediately
      toast('Prompt saved successfully', {type: 'success'});

      // Refetch prompt options in background to update dropdown with new version
      refetchPrompts();
    } catch (error) {
      console.error('Failed to save prompt:', error);
      toast(
        `Failed to save prompt: ${error instanceof Error ? error.message : 'Unknown error'}`,
        {type: 'error'}
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    idx,
    playgroundStates,
    getTsClient,
    refetchPrompts,
    setPlaygroundStateField,
    originalPromptMessages,
  ]);

  const handleCreatePrompt = useCallback(
    async (name: string, messages: Messages) => {
      setIsCreatingPrompt(true);
      try {
        // Create the prompt
        const result = await createNewPrompt({
          projectId: `${entity}/${project}`,
          entity,
          project,
          promptName: name,
          messages,
          objCreate,
          router,
        });

        // Build the prompt ref with the returned digest
        const newPromptRef = objectVersionKeyToRefUri({
          scheme: WEAVE_REF_SCHEME,
          entity,
          project,
          weaveKind: 'object',
          objectId: result.objectId,
          versionHash: result.objectDigest,
          path: '',
          refExtra: '',
        });

        // Deep copy messages to prevent mutation
        const savedMessages = JSON.parse(JSON.stringify(messages));

        // Update state with new prompt - auto-resolve useEffect will update with correct version
        setPlaygroundStateField(idx, 'prompt', {
          promptName: name,
          promptRef: newPromptRef,
          messages: savedMessages,
          isModified: false,
        });

        // Update original messages with the newly created prompt
        originalPromptMessages.current[idx] = savedMessages;

        // Show success
        toast('Prompt created successfully', {type: 'success'});

        // Refetch prompt options in background to update dropdown
        refetchPrompts();

        // Close drawer
        setIsCreatePromptDrawerOpen(false);
      } catch (error) {
        console.error('Failed to create prompt:', error);
        toast(
          `Failed to create prompt: ${error instanceof Error ? error.message : 'Unknown error'}`,
          {type: 'error'}
        );
      } finally {
        setIsCreatingPrompt(false);
      }
    },
    [
      entity,
      project,
      objCreate,
      router,
      idx,
      setPlaygroundStateField,
      originalPromptMessages,
      refetchPrompts,
    ]
  );

  const state = playgroundStates[idx];

  // Auto-resolve promptRef when loading a call with a prompt but no promptName set
  useEffect(() => {
    const promptRef = state.prompt?.promptRef;
    const promptName = state.prompt?.promptName;

    // If we have a promptRef but no promptName (or an incorrect one), resolve it from promptOptions
    if (promptRef && !promptsLoading && promptOptions.length > 0) {
      // Parse the weave URI to extract object ID and digest using proper utility
      const parsedRef = parseRefMaybe(promptRef) as WeaveObjectRef;
      if (parsedRef && parsedRef.scheme === 'weave') {
        const promptObjectId = parsedRef.artifactName;
        const digest = parsedRef.artifactVersion;

        // Find the prompt in promptOptions
        const promptOption = promptOptions.find(
          opt => opt.value === promptObjectId
        );

        if (promptOption) {
          // Try to find the specific version by digest
          const specificVersion = promptOption.versions.find(
            v => v.digest === digest
          );
          const versionToUse = specificVersion || promptOption.latestVersion;

          if (versionToUse) {
            // Only update if the promptName doesn't match what we expect
            if (promptName !== versionToUse.value) {
              setPlaygroundStateField(idx, 'prompt', {
                promptName: versionToUse.value, // Format: "name:vX"
                messages: versionToUse.messages,
                promptRef, // Keep the full weave URI
                isModified: false,
              });
            }
          }
        }
      }
    }
  }, [
    state.prompt?.promptRef,
    state.prompt?.promptName,
    promptOptions,
    promptsLoading,
    idx,
    setPlaygroundStateField,
  ]);

  // Helper function to build tooltip content with non-default settings
  const getSettingsTooltipContent = (state: PlaygroundState) => {
    const nonDefaultSettings: Array<{label: string; value: string}> = [];

    // Check each setting against defaults
    if (state.maxTokens !== DEFAULT_PLAYGROUND_STATE.maxTokens) {
      nonDefaultSettings.push({
        label: 'Max tokens',
        value: state.maxTokens.toString(),
      });
    }
    if (state.temperature !== DEFAULT_PLAYGROUND_STATE.temperature) {
      nonDefaultSettings.push({
        label: 'Temperature',
        value: state.temperature.toString(),
      });
    }
    if (state.topP !== DEFAULT_PLAYGROUND_STATE.topP) {
      nonDefaultSettings.push({label: 'Top P', value: state.topP.toString()});
    }
    if (state.frequencyPenalty !== DEFAULT_PLAYGROUND_STATE.frequencyPenalty) {
      nonDefaultSettings.push({
        label: 'Frequency penalty',
        value: state.frequencyPenalty.toString(),
      });
    }
    if (state.presencePenalty !== DEFAULT_PLAYGROUND_STATE.presencePenalty) {
      nonDefaultSettings.push({
        label: 'Presence penalty',
        value: state.presencePenalty.toString(),
      });
    }
    if (state.nTimes !== DEFAULT_PLAYGROUND_STATE.nTimes) {
      nonDefaultSettings.push({
        label: 'Num trials',
        value: state.nTimes.toString(),
      });
    }
    if (state.responseFormat !== DEFAULT_PLAYGROUND_STATE.responseFormat) {
      nonDefaultSettings.push({
        label: 'Response format',
        value: state.responseFormat,
      });
    }
    if (
      state.functions &&
      state.functions.length > DEFAULT_PLAYGROUND_STATE.functions.length
    ) {
      nonDefaultSettings.push({
        label: 'Functions',
        value: `${state.functions.length} function(s)`,
      });
    }
    if (
      state.stopSequences &&
      state.stopSequences.length > DEFAULT_PLAYGROUND_STATE.stopSequences.length
    ) {
      nonDefaultSettings.push({
        label: 'Stop sequences',
        value: state.stopSequences.join(', '),
      });
    }
    if (state.jsonSchema !== DEFAULT_PLAYGROUND_STATE.jsonSchema) {
      nonDefaultSettings.push({
        label: 'JSON schema',
        value: 'Defined',
      });
    }

    // If all settings are at defaults
    if (nonDefaultSettings.length === 0) {
      return (
        <div className="text-xs text-moon-400">
          All settings at default values
        </div>
      );
    }

    // Return formatted list of non-default settings
    return (
      <div className="flex flex-col gap-1 text-sm">
        {nonDefaultSettings.map((setting, index) => (
          <div
            key={index}
            className="flex items-center gap-8 whitespace-nowrap">
            <span className="text-moon-400">{setting.label}:</span>{' '}
            <span className="ml-auto text-right text-moon-200">
              {setting.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const isLastChat = idx === playgroundStates.length - 1;
  const areMultipleChats = playgroundStates.length > 1;

  return (
    <div className="flex w-full flex-col gap-4">
      {/* First row: Number, model name, Call ID on left; model picker, settings, clear, close on right */}
      <div className="flex w-full justify-between items-center gap-4 px-12 pt-8 pb-4">
        <div className="flex items-center gap-8 min-w-0 overflow-hidden flex-1">
          <Tag label={`${idx + 1}`} />
          <span className="font-semibold truncate min-w-0">
            {playgroundStates[idx].savedModel?.objectId || 'Untitled model'}
          </span>
          {playgroundStates[idx].traceCall?.id && (
            <div className="flex items-center">
              <CopyableId
                id={playgroundStates[idx]!.traceCall!.id!}
                type="Call"
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <LLMDropdown
            value={playgroundStates[idx].model}
            onChange={(model, maxTokens, maxTokensDefault, savedModel) =>
              handleModelChange(
                idx,
                model as LLMMaxTokensKey,
                maxTokens,
                maxTokensDefault,
                savedModel
              )
            }
            entity={entity}
            project={project}
            isTeamAdmin={isTeamAdmin}
            refetchConfiguredProviders={refetchConfiguredProviders}
            refetchCustomLLMs={refetchCustomLLMs}
            llmDropdownOptions={llmDropdownOptions}
            areProvidersLoading={areProvidersLoading}
            customProvidersResult={customProvidersResult}
            direction={{
              horizontal: areMultipleChats && isLastChat ? 'left' : 'right',
            }}
            selectFirstAvailable={true}
            className="w-[300px]"
          />
          <Button
            tooltip={getSettingsTooltipContent(playgroundStates[idx])}
            tooltipProps={{side: 'bottom', align: 'end'}}
            icon="settings-parameters"
            size="medium"
            variant="secondary"
            className="ml-2"
            active={settingsTab === idx}
            onClick={() => {
              if (settingsTab === idx) {
                setSettingsTab(null);
              } else {
                setSettingsTab(idx);
              }
            }}
          />
          <Button
            tooltip={'Clear chat'}
            icon="randomize-reset-reload"
            size="medium"
            variant="secondary"
            onClick={() => setConfirmClear(true)}
          />
          <Button
            endIcon="close"
            size="medium"
            variant="ghost"
            disabled={onlyOneChat}
            tooltip={onlyOneChat ? 'Cannot remove last chat' : 'Remove chat'}
            onClick={() => {
              // If the settings tab is set to length that is going to be removed,
              // we need to set the settings tab to the previous chat.
              if (settingsTab && settingsTab < playgroundStates.length + 1) {
                setSettingsTab(settingsTab - 1);
              }
              setPlaygroundStates(
                playgroundStates.filter((_, index) => index !== idx)
              );
            }}
          />
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-moon-200 mb-8"></div>

      {/* Prompt dropdown */}
      <div className="flex items-center justify-between gap-2 pb-8 px-12">
        <div className="flex items-center gap-8">
          <PromptDropdown
            ref={promptDropdownRef}
            entity={entity}
            project={project}
            value={playgroundStates[idx].prompt?.promptName || ''}
            onChange={handlePromptChangeWithWarning}
            onClear={handlePromptClearWithWarning}
            promptOptions={promptOptions}
            loading={promptsLoading}
            isModified={playgroundStates[idx].prompt?.isModified || false}
          />
          {playgroundStates[idx].prompt?.isModified && (
            <Tag label="Unsaved changes" color="moon" />
          )}
        </div>
        {playgroundStates[idx].prompt?.promptName && (
          <div className="flex items-center gap-4 ml-4">
            {/* Show actions dropdown only for existing prompts (not "New prompt") */}
            {playgroundStates[idx].prompt?.promptName !== NEW_PROMPT_FLAG && (
              <DropdownMenu.Root
                open={isActionsMenuOpen}
                onOpenChange={setIsActionsMenuOpen}>
                <DropdownMenu.Trigger asChild>
                  <Button
                    active={isActionsMenuOpen}
                    variant="ghost"
                    icon="overflow-horizontal"
                  />
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content align="end" style={{zIndex: 10}}>
                    <DropdownMenu.Item
                      onClick={() => {
                        handleResetWithWarning();
                        setIsActionsMenuOpen(false);
                      }}
                      disabled={!playgroundStates[idx].prompt?.isModified}>
                      <Icon name="randomize-reset-reload" /> Reset prompt
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      onClick={() => {
                        handleSaveAsNew();
                        setIsActionsMenuOpen(false);
                      }}>
                      <Icon name="add-new" /> Save as new prompt
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            )}

            {/* Show Save button - enabled when modified (for both new and existing) */}
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!playgroundStates[idx].prompt?.isModified || isSaving}
              size="medium">
              {isSaving ? 'Saving...' : 'Save prompt'}
            </Button>
          </div>
        )}
      </div>
      <ConfirmClearModal
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={() => {
          clearCall(idx);
          setConfirmClear(false);
        }}
      />
      <ConfirmPromptChangeModal
        open={confirmPromptChange}
        onClose={() => {
          setConfirmPromptChange(false);
          setPendingPromptAction(null);
        }}
        onConfirm={handleConfirmPromptChange}
      />
      <CreatePromptDrawer
        open={isCreatePromptDrawerOpen}
        onClose={() => setIsCreatePromptDrawerOpen(false)}
        onSavePrompt={handleCreatePrompt}
        isCreating={isCreatingPrompt}
        initialMessages={createPromptMessages}
      />
    </div>
  );
};
